import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Slider } from '@mui/material';
import { saveAs } from 'file-saver';
import { useChowaTheme } from '../../config/chowaThemeContext';
import './calendar.css';

const TemperatureChart = () => {
  const { isDark } = useChowaTheme();
  const [data, setData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [zoomDomain, setZoomDomain] = useState([0, 24]);
  const [availableDates, setAvailableDates] = useState(new Set());
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // TMSでは常にデフォルトのグレー色を使用（シリーズ選択の影響を受けない）
  const defaultTheme = {
    primary: isDark ? '#6b7280' : '#4b5563', // グレー
    light: isDark ? '#374151' : '#f3f4f6',
    dark: isDark ? '#111827' : '#1f2937'
  };

  const fetchAvailableDates = useCallback(async (year, month) => {
    try {
      const months = [-1, 0, 1]; // 前月、当月、翌月
      const fetchPromises = months.map(offset => {
        const targetDate = new Date(year, month + offset, 1);
        const targetYear = targetDate.getFullYear();
        const targetMonth = targetDate.getMonth() + 1; // APIは1-12の月を期待
        return fetch(`${process.env.REACT_APP_TMS000_CHART_API}/available-date/?year=${targetYear}&month=${targetMonth}`);
      });

      const responses = await Promise.all(fetchPromises);
      const jsonDataPromises = responses.map(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch available dates');
        }
        return response.json();
      });

      const jsonDataArray = await Promise.all(jsonDataPromises);

      const allAvailableDates = new Set();
      jsonDataArray.forEach(data => {
        if (data.available_dates && Array.isArray(data.available_dates)) {
          data.available_dates.forEach(date => allAvailableDates.add(new Date(date).toDateString()));
        }
      });

      setAvailableDates(allAvailableDates);
      console.log('All available dates:', allAvailableDates);
    } catch (error) {
      console.error('Error fetching available dates:', error);
    }
  }, []);

  useEffect(() => {
    fetchAvailableDates(currentMonth.getFullYear(), currentMonth.getMonth());
  }, [currentMonth, fetchAvailableDates]);

  const fetchData = useCallback(async (date) => {
    try {
      const jstDateString = date.toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).replace(/\//g, '-');

      console.log('Selected date:', date);
      console.log('Date string for API (JST):', jstDateString);

      const apiUrl = `${process.env.REACT_APP_TMS000_CHART_API}/?date=${jstDateString}`;
      console.log('API request URL:', apiUrl);

      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const jsonData = await response.json();
      console.log('Fetched data:', jsonData);

      setData(jsonData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
    }
  }, []);

  useEffect(() => {
    fetchData(selectedDate);
  }, [selectedDate, fetchData]);

  const handleDateChange = (date) => {
    console.log('Date changed to:', date);
    setSelectedDate(date);
  };

  const handleMonthChange = (date) => {
    console.log('Month changed to:', date);
    setCurrentMonth(date);
  };

  const handleZoomChange = (event, newValue) => {
    setZoomDomain(newValue);
  };

  const handleResetZoom = () => {
    setZoomDomain([0, 24]);
  };

  const formatXAxis = (time) => {
    return time.split(':').slice(0, 2).join(':');
  };

  const visibleData = data.filter((item) => {
    const hour = parseInt(item.time.split(':')[0], 10);
    return hour >= zoomDomain[0] && hour < zoomDomain[1];
  });

  const downloadCSV = () => {
    const csvRows = [];
    const headers = ['Date', 'Time', 'Temperature'];
    csvRows.push(headers.join(','));

    visibleData.forEach(row => {
      const values = [row.date, row.time, row.temperature];
      csvRows.push(values.join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `temperature_data_${selectedDate.toISOString().split('T')[0]}.csv`);
  };

  const highlightDates = (date) => {
    const dateString = date.toDateString();
    return availableDates.has(dateString) ? "has-data" : undefined;
  };

  console.log('Visible data:', visibleData);

  return (
    <div className="flex-1 min-h-0 w-full h-full">
      <div className="flex flex-col h-full">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-2 lg:space-y-0 mb-4">
          <div className="flex items-center">
            <label htmlFor="date-picker" className="text-sm lg:text-base text-gray-700 dark:text-gray-300">
              日付選択:
            </label>
            <DatePicker
              id="date-picker"
              selected={selectedDate}
              onChange={handleDateChange}
              onMonthChange={handleMonthChange}
              dateFormat="yyyy-MM-dd"
              className="ml-2 text-sm lg:text-base p-1.5 lg:p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500"
              dayClassName={highlightDates}
              showMonthYearPicker={false}
            />
          </div>
          <button
            onClick={downloadCSV}
            disabled={visibleData.length === 0}
            className="px-3 py-1.5 text-sm lg:text-base bg-green-600 hover:bg-green-700 text-white font-medium rounded transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            CSVをダウンロード
          </button>
        </div>

        <div className="flex-1 min-h-0 relative">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={visibleData}
              margin={{ top: 10, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}
              />
              <XAxis
                dataKey="time"
                type="category"
                tickFormatter={formatXAxis}
                stroke={isDark ? '#9ca3af' : '#4b5563'}
                tick={{ fill: isDark ? '#9ca3af' : '#4b5563', fontSize: '0.75rem' }}
                label={{
                  value: 'timestamp[HH:MM]',
                  position: 'insideBottom',
                  offset: -10,
                  style: { textAnchor: 'middle', fontWeight: 'bold', fontSize: '0.75rem' }
                }}
              />
              <YAxis
                domain={['dataMin - 1', 'dataMax + 1']}
                stroke={isDark ? '#9ca3af' : '#4b5563'}
                tick={{ fill: isDark ? '#9ca3af' : '#4b5563', fontSize: '0.75rem' }}
                label={{
                  value: 'temperature (°C)',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -35,
                  style: { textAnchor: 'middle', fontWeight: 'bold', fontSize: '0.75rem' }
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDark ? '#1f2937' : '#ffffff',
                  border: '1px solid',
                  borderColor: isDark ? '#374151' : '#e5e7eb',
                  fontSize: '0.75rem',
                  padding: '0.5rem'
                }}
              />
              <Line
                type="monotone"
                dataKey="temperature"
                stroke={defaultTheme.primary}
                dot={false}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>

          {(!visibleData || visibleData.length === 0) && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-gray-500 dark:text-gray-400 text-lg">
                選択された日付のデータがありません。
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm lg:text-base font-medium text-gray-700 dark:text-gray-300">
              ズーム範囲:
            </span>
            <button
              onClick={handleResetZoom}
              className="px-3 py-1 text-sm lg:text-base hover:bg-gray-600 text-white rounded transition-colors"
              style={{ backgroundColor: defaultTheme.primary }}
            >
              ズームをリセット
            </button>
          </div>
          <Slider
            value={zoomDomain}
            onChange={handleZoomChange}
            min={0}
            max={24}
            step={1}
            marks={[
              { value: 0, label: '0:00' },
              { value: 6, label: '6:00' },
              { value: 12, label: '12:00' },
              { value: 18, label: '18:00' },
              { value: 24, label: '24:00' }
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${value}:00`}
            sx={{
              '& .MuiSlider-rail': {
                backgroundColor: isDark ? '#374151' : '#e5e7eb',
              },
              '& .MuiSlider-track': {
                backgroundColor: defaultTheme.primary,
              },
              '& .MuiSlider-thumb': {
                backgroundColor: defaultTheme.primary,
              },
              '& .MuiSlider-mark': {
                backgroundColor: isDark ? '#4b5563' : '#9ca3af',
              },
              '& .MuiSlider-markLabel': {
                color: isDark ? '#9ca3af' : '#4b5563',
                fontSize: '0.75rem',
              },
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default TemperatureChart;